import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import React, { createContext, useCallback, useContext, useDebugValue, useEffect, useMemo, useState } from 'react';
import { ISlot, MaintenanceTimingEnum } from '../../organisms/bo_calendar_slot/types';
import { ECalendarMode, IOptions, ISlotsGroup, TOnChangeInCalender } from '../types';
import {
	calculateMarginFromHours,
	calculateMarginFromMinutes,
	calculateMarginLeftFromHours,
	calculateMarginLeftFromMinutes,
	checkForIntersectionWithDate,
	HOURS_PER_DAY,
	oneHourHeight,
	oneHourWidth,
	setNewHourHeight,
	setNewHourWidth,
} from '../utils/timeUtils';
import { setSpaceHeight, setSpaceWidth } from '../atoms/styles';
import { DateTimeFormats, TimeUnit } from '@bondsports/date-time';
import { calculateOverallEndTime, calculateOverallStartTime, getCombinedDateTimeString } from '../../lib/timeUtils';
import { maxBy, minBy } from 'lodash';
import { ResourceTypeEnum } from '../../types/enums/resource';
import { useUpdateSlot } from './useUpdateSlot';
import { getSubGroups, splitIntoGroups } from '../utils/groups';

dayjs.extend(isoWeek);

export type DateRange = {
	[key: number]: {
		startDate: string;
		endDate: string;
	};
};

const BEFORE_AND_AFTER_MAINTENANCE = [MaintenanceTimingEnum.AFTER, MaintenanceTimingEnum.BEFORE] as const;

export function updateChildrenStartAndEndTimes(children: ISlot[], delta: number, daysDelta?: number) {
	for (const child of children ?? []) {
		// add the drag n' drop change delta minutes to the start date and time
		const childStartDate = dayjs
			.utc(getCombinedDateTimeString(child.startDate, child.startTime))
			.add(delta, 'minutes')
			.add(daysDelta ?? 0, 'days');

		// add the drag n' drop change delta minutes to the end date and time
		const childEndDate = dayjs
			.utc(getCombinedDateTimeString(child.endDate, child.endTime))
			.add(delta, 'minutes')
			.add(daysDelta ?? 0, 'days');

		child.startDate = childStartDate.format(DateTimeFormats.YYYY_MM_DD);
		child.startTime = childStartDate.format(DateTimeFormats.H24_WITH_SECONDS);

		child.endDate = childEndDate.format(DateTimeFormats.YYYY_MM_DD);
		child.endTime = childEndDate.format(DateTimeFormats.H24_WITH_SECONDS);
	}
}

export type MiddlewareContent = {
	dispatchChange?: TOnChangeInCalender;
	day: number;
	incrementDay: () => void;
	options: IOptions;
	getPartialSlots: (slots: ISlot[], from: number, to: number) => ISlot[];
	getTopAndHeight: (
		startDate: number,
		endDate: number,
		horizontal?: boolean,
		selectedDate?: string,
		debug?: boolean
	) => { top: number; height: number };
	getSlotGroups: (slots: ISlot[], horizontal?: boolean, date?: string) => ISlotsGroup[];
	handleDrop: (
		slot: ISlot,
		slotId: number | string,
		parentId: number | string,
		spaceId: number | string,
		newDate: string,
		differenceInMinutes: number,
		differenceInDays?: number,
		newEndDate?: string
	) => void;
};

const MiddlewareContext = createContext<MiddlewareContent | null>(null);

type Props = {
	onChange?: TOnChangeInCalender;
	options: IOptions;
};

export const MiddlewareContextProvider: React.FC<Props> = ({ children, onChange, options }) => {
	const { updateResourceSlot } = useUpdateSlot();

	const currentDate: string = options?.date ?? '';

	const handleDrop = useCallback(
		(
			slot: ISlot,
			slotId: number | string,
			parentId: number | string,
			resourceId: number | string,
			newDate: string,
			differenceInMinutes: number,
			differenceInDays?: number,
			newEndDate?: string
		) => {
			let updatedSlot: ISlot = { ...slot, resourceId: String(resourceId) };

			let startDate: string = dayjs.utc(newDate).format(DateTimeFormats.YYYY_MM_DD);
			let startTime: string = slot.startTime;

			let endDate: string =
				slot.endDate > slot.startDate
					? dayjs.utc(startDate).add(1, TimeUnit.DAY).format(DateTimeFormats.YYYY_MM_DD)
					: slot.endDate;
			let endTime: string = slot.endTime;

			let overallStartTime: string = slot.overallStartTime!;
			let overallEndTime: string = slot.overallEndTime!;

			if (newDate && (options.mode === ECalendarMode.DAILY || options.mode === ECalendarMode.DAYX)) {
				const date: dayjs.Dayjs = dayjs(newDate);
				const requestedDate: number = date.get('date');
				const requestedMonth: number = date.get('month');
				const requestedYear: number = date.get('year');

				startDate = dayjs(updatedSlot.startDate)
					.set('date', requestedDate)
					.set('month', requestedMonth)
					.set('year', requestedYear)
					.format();

				endDate = dayjs(updatedSlot.endDate)
					.set('date', requestedDate)
					.set('month', requestedMonth)
					.set('year', requestedYear)
					.format();

				// Drag 'n' drop slot on daily view
				if (newEndDate) {
					startDate = newDate;
					endDate = newEndDate;
				}

				// formatting startDate to YYYY-MM-DD and startTime to HH:mm:ss
				startTime = dayjs.utc(startDate).format(DateTimeFormats.H24_WITH_SECONDS);
				startDate = dayjs.utc(startDate).format(DateTimeFormats.YYYY_MM_DD);

				// formatting endDate to YYYY-MM-DD and endTime to HH:mm:ss
				endTime = dayjs.utc(endDate).format(DateTimeFormats.H24_WITH_SECONDS);
				endDate = dayjs.utc(endDate).format(DateTimeFormats.YYYY_MM_DD);

				updateChildrenStartAndEndTimes(updatedSlot.children!, differenceInMinutes, differenceInDays);

				// ignore maintenance slots at the beginning and end of a slot as they do not affect the slot overall time
				const children: ISlot[] =
					updatedSlot.children?.filter((child: ISlot) =>
						BEFORE_AND_AFTER_MAINTENANCE.includes(Number(child.maintenanceTiming))
					) ?? [];

				// get the lowest startDate and the highest endDate from the slot and its children
				const minStartDate: string = minBy([startDate, ...children.map((child: ISlot) => child.startDate)])!;
				const maxEndDate: string = maxBy([endDate, ...children.map((child: ISlot) => child.endDate)])!;

				// calculating overallStartTime
				overallStartTime = calculateOverallStartTime(
					[
						getCombinedDateTimeString(startDate, startTime),
						...children.map((child: ISlot) => getCombinedDateTimeString(child.startDate, child.startTime)),
					],
					minStartDate,
					options.mode === ECalendarMode.DAYX ? date : currentDate
				);
				// calculating overallEndTime
				overallEndTime = calculateOverallEndTime(
					[
						getCombinedDateTimeString(endDate, endTime),
						...children.map((child: ISlot) => getCombinedDateTimeString(child.endDate, child.endTime)),
					],
					maxEndDate,
					options.mode === ECalendarMode.DAYX ? date : currentDate
				);
			}

			updatedSlot = {
				...updatedSlot,
				startDate,
				startTime,
				endDate,
				endTime,
				overallStartTime,
				overallEndTime,
				children: updatedSlot.children,
			};

			if (updatedSlot.resourceType === ResourceTypeEnum.SPACE) {
				updatedSlot.spaceId = Number(resourceId);
				updatedSlot.space.id = Number(resourceId);
			}

			updateResourceSlot(Number(parentId), updatedSlot);

			return updatedSlot;
		},
		[currentDate, updateResourceSlot, options.mode]
	);

	/**
	 * TODO: Re-evaluate when fixing infinite scrolling
	 * Used for infinite scrolling (currently does not work)
	 */
	const [day, setDay] = useState(1);

	/**
	 * TODO: Re-evaluate when fixing infinite scrolling
	 * Used for infinite scrolling (currently does not work)
	 */
	const incrementDay = useCallback(() => {
		setDay(currentDay => {
			onChange?.({
				type: 'SCROLL_TO_NEXT_DAY',
				data: {
					date: dayjs(currentDate).add(currentDay, 'day').format('DD/MM/YYYY'),
				},
			});

			return currentDay + 1;
		});
	}, [onChange]);

	/**
	 * TODO: Re-evaluate when fixing infinite scrolling
	 * Used for infinite scrolling (currently does not work)
	 */
	useEffect(() => {
		setDay(1);

		if (options.infiniteScrolling && (options.mode === ECalendarMode.DAILY || options.mode === ECalendarMode.DAYX)) {
			incrementDay();
		}
	}, [options.mode, options.view, options.date, options.infiniteScrolling]);

	/**
	 * @deprecated - Not used, Might be removed
	 */
	const getPartialSlots = useCallback(
		(slots: ISlot[], from: number, to: number) =>
			slots.filter(slot => {
				const date = dayjs(currentDate);

				const difference = dayjs(slot.startDate).diff(date, 'hour');

				return difference >= from && difference <= to;
			}),
		[currentDate]
	);

	/** Calculating top/left position and height/width of slots for daily mode */
	const getTopAndHeight = useCallback(
		(startDate: number, endDate: number, horizontal?: boolean, selectedDate?: string, debug?: boolean) => {
			const [hour, minutes] = dayjs(startDate).format(DateTimeFormats.H24_WITH_MINUTES).split(':').map(Number);

			// calculating the difference between the start date and end date in minutes
			const diff: number = dayjs(endDate).diff(dayjs(startDate), TimeUnit.MINUTE);

			// calculating the height of the slot
			const height: number = horizontal ? calculateMarginLeftFromMinutes(diff) : calculateMarginFromMinutes(diff);

			// calculating the hour position of the slot
			const marginTop: number = horizontal ? calculateMarginLeftFromHours(hour) : calculateMarginFromHours(hour);

			// calculating the minutes position of the slot
			const marginTopMinutes: number = horizontal
				? calculateMarginLeftFromMinutes(minutes)
				: calculateMarginFromMinutes(minutes);

			// calculating the position of the slot
			const total: number = marginTop + marginTopMinutes;

			const size: number = horizontal ? oneHourWidth : oneHourHeight;
			const shift: number =
				size *
				HOURS_PER_DAY *
				dayjs(startDate)
					.startOf('day')
					.diff(dayjs(selectedDate ?? currentDate).startOf('day'), TimeUnit.DAY);

			return { height: height || 0, top: total + shift || shift };
		},
		[currentDate]
	);

	/** Making slots groups with collision */
	const getSlotGroups = useCallback(
		(slots: ISlot[], horizontal: boolean, date: string): ISlotsGroup[] => {
			const slotToView: ISlot[] = slots.filter(slot => checkForIntersectionWithDate(slot, date));

			const groups: ISlot[][] = splitIntoGroups(slotToView, date);

			const result: ISlotsGroup[] = [];

			for (const slots of groups) {
				if (slots.length > 1) {
					const startDates: number[] = slots.map(slot => {
						const [hours, minutes] = slot.startTime.split(':').map(Number);
						return dayjs(dayjs.utc(slot.startDate).format('DD/MM/YYYY'), 'DD/MM/YYYY')
							.hour(hours)
							.minute(minutes)
							.toDate()
							.getTime();
					});

					const endDates: number[] = slots.map(slot => {
						const [hours, minutes] = slot.endTime.split(':').map(Number);
						return dayjs(dayjs.utc(slot.endDate).format('DD/MM/YYYY'), 'DD/MM/YYYY')
							.hour(hours)
							.minute(minutes)
							.toDate()
							.getTime();
					});

					let minStart: number = Math.min(...startDates);
					let maxEnd: number = Math.max(...endDates);

					const endOfDay: number = dayjs(date).endOf('day').valueOf();
					if (maxEnd > endOfDay) {
						maxEnd = endOfDay;
					}

					const startOfDay: number = dayjs(date).startOf('day').valueOf();
					if (minStart < startOfDay) {
						minStart = startOfDay;
					}

					const { top, height } = getTopAndHeight(minStart, maxEnd, horizontal, date);

					const groups: ISlot[][] =
						slots.length > 2
							? getSubGroups(slots, date)
							: // The slots id may be an uuid or a number, so we need to convert it to string and use localeCompare to sort
								[[slots[0]], [slots[1]]].sort((a, b) => String(a[0].id).localeCompare(String(b[0].id)));

					result.push({
						// The slots id may be an uuid or a number, so we need to convert it to string and use localeCompare to sort
						items: slots.sort((a, b) => String(a.id).localeCompare(String(b.id))),
						groups,
						top,
						height,
					});
				} else if (slots.length === 1) {
					result.push({ items: slots, item: slots[0] });
				}
			}

			return result;
		},
		[getTopAndHeight]
	);

	const context: MiddlewareContent = useMemo(() => {
		return {
			options,
			day,
			dispatchChange: onChange,
			incrementDay,
			getPartialSlots,
			getTopAndHeight,
			getSlotGroups,
			handleDrop,
		};
	}, [options, day, onChange, incrementDay, getPartialSlots, getTopAndHeight, getSlotGroups, handleDrop]);

	syncCalendarLayoutMetrics(options);

	return <MiddlewareContext.Provider value={context}>{children}</MiddlewareContext.Provider>;
};

/** Syncs hour/space module metrics during render (legacy hourSize pattern). */
function syncCalendarLayoutMetrics(options: IOptions): void {
	setNewHourHeight(options?.hourSize?.vertical);
	setNewHourWidth(options?.hourSize?.horizontal);
	setSpaceWidth(options?.resourceSize?.width);
	setSpaceHeight(options?.resourceSize?.height);
}

MiddlewareContextProvider.displayName = 'MiddlewareContextProvider';

export const useMiddlewareContext = () => {
	useDebugValue('useMiddlewareContext');

	return useContext(MiddlewareContext)!;
};
